'use client';

import { Slot } from '@radix-ui/react-slot';
import { type VariantProps, cva } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '@/lib/utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground hover:bg-primary/90',
        destructive:
          'bg-destructive text-destructive-foreground hover:bg-destructive/90',
        outline:
          'border border-input bg-background text-foreground hover:bg-accent hover:text-accent-foreground',
        secondary:
          'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
        brand:
          'bg-brand-primary-light text-brand-primary-color hover:bg-brand-primary-color hover:text-content-inverse dark:bg-brand-primary dark:text-content-inverse dark:hover:bg-brand-primary/90',
        accent:
          'bg-brand-secondary-color/10 text-content-secondary hover:bg-brand-secondary-color hover:text-content-inverse',
        // Brand outline: outlined button with brand tertiary (brass) border
        brandOutline:
          'border-2 border-brand-tertiary-color bg-surface-subtle text-brand-tertiary-color hover:bg-brand-tertiary-color hover:text-content-inverse transition-all duration-300 rounded-2xl shadow-sm hover:shadow-md',
        // Brand gradient: filled button with brand gradient (purple to blue)
        // Note: uses the arbitrary `background:` property (not `bg-[]`) because
        // gradients need `background`, not `background-color`. Do not spell the
        // bracketed form out here: Tailwind's content scanner reads class-shaped
        // tokens out of comments too, and an empty `var()` variant emitted from
        // this line fails Turbopack's strict CSS parser (`next dev` 500s).
        // #8795: was text-content-inverse — var(--text-inverse) flips to
        // near-black (#141413) in the default/calm/warm dark palettes
        // (unlayered `.dark` wins over the `@layer base` white override),
        // measured 2.85-3.41:1 against this gradient in those combos, plus
        // 4.49:1 (just under AA) in warm-light. text-cta-text (--cta-text)
        // resolves to solid #ffffff in all 8 palette/mode combinations and
        // clears AA everywhere (4.73-6.31:1). See PR body for the full table.
        brandGradient:
          '[background:var(--gradient-button)] text-cta-text font-semibold hover:opacity-90 transition-all duration-300 rounded-2xl shadow-md hover:shadow-lg hover:scale-[1.02]',
      },
      size: {
        default: 'h-10 px-4 py-2',
        sm: 'h-9 px-3',
        lg: 'h-11 px-8',
        // WCAG 2.5.5 / repo 44×44 px touch-target floor (#5942). Bare
        // `size="icon"` now renders a genuine 44 px box by default. Call
        // sites that need a smaller VISUAL footprint in dense chrome still
        // override `h-/w-` here, but should add `min-h-11 min-w-11` (the
        // floor survives the override — tailwind-merge treats `min-h-/min-w-`
        // as a distinct group from `h-/w-`) or use HIT_AREA_44 to keep the
        // 44 px tap target. Base focus-visible ring is in the shared classes
        // above.
        icon: 'h-11 w-11',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  enable3dEffect?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      enable3dEffect = false,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : 'button';

    // State for hover and active effects
    const [isHovered, setIsHovered] = React.useState(false);
    const [isActive, setIsActive] = React.useState(false);

    // Common styles object for 3D effects
    const styleProps: React.CSSProperties = enable3dEffect
      ? {
          transition: 'all 0.2s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
          transform: isActive
            ? 'scale(0.95) translateY(0)'
            : isHovered
              ? 'scale(1.05) translateY(-2px)'
              : 'scale(1) translateY(0)',
          boxShadow: isHovered
            ? '0 6px 10px rgba(0, 0, 0, 0.15)'
            : '0 2px 4px rgba(0, 0, 0, 0.1)',
        }
      : {};

    const eventHandlers = enable3dEffect
      ? {
          onMouseEnter: () => setIsHovered(true),
          onMouseLeave: () => {
            setIsHovered(false);
            setIsActive(false);
          },
          onMouseDown: () => setIsActive(true),
          onMouseUp: () => setIsActive(false),
          onTouchStart: () => setIsActive(true),
          onTouchEnd: () => setIsActive(false),
        }
      : {};

    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        style={{
          ...styleProps,
          ...props.style,
        }}
        {...eventHandlers}
        {...props}
      />
    );
  }
);
Button.displayName = 'Button';

export { Button, buttonVariants };
